import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STORAGE_KEY = "eq_gabriel_session";

function loadOrCreateSession() {
  let s = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  if (!s) {
    s =
      "gabriel-" +
      (window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36));
    try {
      localStorage.setItem(STORAGE_KEY, s);
    } catch {}
  }
  return s;
}

const INITIAL_GREETING =
  "Bonjour, je suis Gabriel, l'assistant virtuel du Groupe Equinoxe. Comment puis-je vous aider ?";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(false);
  const [sessionId] = useState(() => loadOrCreateSession());
  const [messages, setMessages] = useState([
    { role: "assistant", content: INITIAL_GREETING },
  ]);
  const [identifyEmail, setIdentifyEmail] = useState("");
  const [identifyPhone, setIdentifyPhone] = useState("");
  const [customerName, setCustomerName] = useState(null);
  const scrollRef = useRef(null);

  // Ouverture automatique au premier chargement (1 fois par session navigateur)
  useEffect(() => {
    const KEY = "eq_gabriel_autoopened";
    try {
      if (!sessionStorage.getItem(KEY)) {
        const t = setTimeout(() => {
          setOpen(true);
          sessionStorage.setItem(KEY, "1");
        }, 1800);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const extractContacts = (text) => {
    const emailMatch = text.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
    const phoneMatch = text.match(/(?:\+33|0)\s?[1-9](?:[\s.-]?\d{2}){4}/);
    return {
      email: emailMatch ? emailMatch[0].toLowerCase() : null,
      phone: phoneMatch ? phoneMatch[0].replace(/\s|\./g, "") : null,
    };
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);

    // Auto-detect email/phone in user message to enrich identify context
    const { email, phone } = extractContacts(text);
    let newEmail = identifyEmail || email || "";
    let newPhone = identifyPhone || phone || "";
    if (email && !identifyEmail) setIdentifyEmail(email);
    if (phone && !identifyPhone) setIdentifyPhone(phone);

    try {
      const { data } = await api.post("/chat/message", {
        session_id: sessionId,
        message: text,
        history: messages.slice(-30),
        identify_email: newEmail || null,
        identify_phone: newPhone || null,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.customer_identified && data.customer_name) {
        setCustomerName(data.customer_name);
      }
      if (!open) setUnread(true);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Désolé, je rencontre un souci technique. Vous pouvez nous joindre au 05 32 18 90 44 ou via le formulaire /contact.",
        },
      ]);
      // eslint-disable-next-line no-console
      console.warn("chat error:", formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => {
          setOpen((o) => !o);
          setUnread(false);
        }}
        data-testid="gabriel-toggle"
        aria-label={open ? "Fermer Gabriel" : "Ouvrir Gabriel"}
        className="fixed z-[60] bottom-6 right-6 h-14 w-14 rounded-full bg-white text-black flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
        style={{ boxShadow: "0 10px 40px -10px rgba(255,255,255,0.4), 0 0 0 1px rgba(0,0,0,0.4)" }}
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
        {!open && unread && (
          <span className="absolute top-1 right-1 h-3 w-3 rounded-full bg-rose-500 ring-2 ring-black" />
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="gabriel-panel"
          className="fixed z-[60] bottom-24 right-6 w-[92vw] sm:w-[400px] max-h-[78vh] flex flex-col rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a] shadow-2xl"
          style={{ boxShadow: "0 24px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)" }}
        >
          {/* Header */}
          <div className="relative px-5 py-4 bg-black border-b border-white/10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Assistant virtuel
                </div>
                <div className="font-display text-xl font-semibold text-white tracking-tight">
                  Gabriel
                </div>
                {customerName && (
                  <div className="text-xs text-emerald-400 mt-1" data-testid="gabriel-customer-tag">
                    Bonjour {customerName}
                  </div>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Fermer"
                className="text-zinc-500 hover:text-white"
                data-testid="gabriel-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-[2px] flex">
              <div className="flex-1" style={{ backgroundColor: "#8b5cf6" }} />
              <div className="flex-1" style={{ backgroundColor: "#ea580c" }} />
              <div className="flex-1" style={{ backgroundColor: "#0ea5e9" }} />
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#070707]"
            data-testid="gabriel-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`gabriel-msg-${m.role}`}
              >
                <div
                  className={`max-w-[85%] text-sm leading-relaxed px-3.5 py-2.5 rounded-2xl whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-white text-black rounded-br-sm"
                      : "bg-[#161616] text-zinc-100 border border-white/5 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="gabriel-typing">
                <div className="bg-[#161616] border border-white/5 rounded-2xl rounded-bl-sm px-4 py-3 text-zinc-500 text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  <span>Gabriel rédige…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="px-3 py-3 bg-black border-t border-white/10">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Écrivez votre message…"
                rows={1}
                data-testid="gabriel-input"
                className="flex-1 resize-none bg-[#141414] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-white/30 max-h-32"
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                data-testid="gabriel-send"
                aria-label="Envoyer"
                className="h-10 w-10 flex items-center justify-center rounded-xl bg-white text-black disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="mt-2 text-[10px] text-zinc-600 text-center">
              Vos données ne sont utilisées que pour répondre à votre demande.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
